import {
    ConfigFieldType,
    Fields,
    FieldStatus,
    RulesRHF,
    SelectorFieldDataImpl,
    SelectorOption,
    SelectorValueOptions,
} from 'adapter/in/common/form/types';
import { useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { PresetFieldDependency } from 'use-case/presets/fields/read/readPresetFieldCriteria';

// Build Selector FieldData
const clear = (initialValue: string, configType: ConfigFieldType): SelectorFieldDataImpl =>
    buildSelectorFieldData(FieldStatus.RESET, initialValue, configType);

export const buildSelectorFieldData = (
    status: FieldStatus,
    initialValue = '',
    configType: ConfigFieldType = ConfigFieldType.DATA,
    value: SelectorValueOptions = []
): SelectorFieldDataImpl => ({
    status,
    initialValue,
    value,
    serialize: () =>
        Array.isArray(value) ? value.map((selectOption: SelectorOption) => selectOption?.value).join() : (value as SelectorOption).value,
    hasValue: () => (Array.isArray(value) ? !!value.length : (value as SelectorOption).value !== ''),
    getInitialValue: () => initialValue.split(','),
    clear: () => clear(initialValue, configType),
    type: configType,
});

export const isSelectorFieldDataValid = (minSelection: number, maxSelection: number) => (value: SelectorFieldDataImpl) =>
    (value?.status !== FieldStatus.INVALID && value?.value && value.value.length >= minSelection && value.value.length <= maxSelection) ||
    value?.status === FieldStatus.DEFAULT_DATA;

const shouldHaveValidValue = (dependantValue: SelectorFieldDataImpl) =>
    dependantValue !== undefined && dependantValue.status === FieldStatus.HAS_VALUE;
const refreshableStatus = [FieldStatus.INIT, FieldStatus.INITIAL_VALUE, FieldStatus.RESET];
const haveDependenciesAndValidStatus = (dependantValues: SelectorFieldDataImpl[], fieldStatus: FieldStatus) =>
    dependantValues && dependantValues.length > 0 && dependantValues.every(shouldHaveValidValue) && refreshableStatus.includes(fieldStatus);
const noDependenciesAndValidStatus = (dependantValues: SelectorFieldDataImpl[], fieldStatus: FieldStatus) =>
    (!dependantValues || dependantValues.length === 0) && refreshableStatus.includes(fieldStatus);

export const useBaseSelectorContainer = (
    name: string,
    rules: RulesRHF = {
        required: true,
        // Right now, it stays as valid every value entered, as an empty value may be acceptable
        validate: () => true,
    },
    defaultValue?: SelectorFieldDataImpl,
    dataDependsOn?: string[],
    affectsTo?: string[],
    onChange?: (value: unknown) => void
) => {
    const { register, control, getValues, trigger, watch, setValue, clearErrors, setError } = useFormContext<
        Fields<SelectorFieldDataImpl>
    >();
    const valueForm = watch(name);
    const { field, fieldState } = useController({
        name,
        rules,
        control,
        defaultValue: defaultValue || valueForm || buildSelectorFieldData(FieldStatus.INIT, '', ConfigFieldType.DATA, []),
    });
    const [shouldLoad, setShouldLoad] = useState(false);
    const dependantValues = watch(dataDependsOn ?? []);
    const dependencies = useMemo(
        () => (dataDependsOn ? dataDependsOn?.map((value) => new PresetFieldDependency(value, getValues(value).serialize())) : []),
        [dependantValues, dataDependsOn]
    );

    useEffect(() => {
        setError(name, { type: 'loading' });
    }, []);

    useEffect(() => {
        if (
            haveDependenciesAndValidStatus(dependantValues, field.value.status) ||
            noDependenciesAndValidStatus(dependantValues, field.value.status)
        ) {
            setShouldLoad(true);
        } else {
            setShouldLoad(false);
        }
    }, [dependantValues, field.value.status]);

    const refreshAffectedFields = () => {
        clearErrors(name);
        if (affectsTo && affectsTo.length > 0) {
            affectsTo.forEach((fieldToRefresh) => {
                clearErrors(fieldToRefresh);
                setValue(
                    fieldToRefresh,
                    buildSelectorFieldData(FieldStatus.RESET, getValues(fieldToRefresh).initialValue, ConfigFieldType.DATA)
                );
            });
        }
    };

    const callbackOnChange = (value: SelectorFieldDataImpl) => {
        clearErrors(name);
        if (affectsTo && affectsTo.length > 0) refreshAffectedFields();
        field.onChange(value);
        if (onChange) onChange(value);
        trigger(name);
    };

    return {
        value: field.value as SelectorFieldDataImpl,
        error: fieldState.error,
        invalid: fieldState.invalid,
        onChange: callbackOnChange,
        shouldLoad,
        register,
        trigger,
        dependantValues: dependencies && dependencies.length > 0 ? dependencies : [],
    };
};
